import { ServicePromotion, PromoLimitType } from '../types';
import { formatCurrencyBRL } from './formatUtils';
import { getLocalDateString } from '../modules/erp/utils/calendarUtils';

/**
 * Calcula o percentual de desconto de forma segura e arredondada.
 * Ex: De R$ 150 por R$ 120 -> 20%
 */
export function calculateDiscountPercent(originalPrice: number, promoPrice: number): number {
  if (!originalPrice || originalPrice <= 0 || !promoPrice || promoPrice <= 0) {
    return 0;
  }
  if (promoPrice >= originalPrice) {
    return 0;
  }
  const discount = ((originalPrice - promoPrice) / originalPrice) * 100;
  return Math.round(discount);
}

/**
 * Retorna o valor em Reais economizado pelo cliente.
 */
export function calculateDiscountAmount(originalPrice: number, promoPrice: number): number {
  if (!originalPrice || !promoPrice || promoPrice >= originalPrice) {
    return 0;
  }
  return Math.max(0, originalPrice - promoPrice);
}

/**
 * Verifica se a promoção de um serviço está ativa e válida (dentro das cotas ou datas).
 */
export function isPromoActive(
  promo?: ServicePromotion | null,
  referenceDate?: string | Date
): boolean {
  if (!promo || !promo.active) {
    return false;
  }

  if (typeof promo.promoPrice !== 'number' || promo.promoPrice <= 0) {
    return false;
  }

  const limitType: PromoLimitType = promo.limitType || 'none';

  // 1. Limite por quantidade de atendimentos / vagas
  if (limitType === 'quantity') {
    const max = Number(promo.maxUsage || 0);
    const current = Number(promo.currentUsage || 0);
    if (max > 0 && current >= max) {
      return false; // Vagas esgotadas
    }
  }

  // 2. Limite por data / período
  if (limitType === 'date' && promo.endDate) {
    // Normaliza para início do dia de comparação no fuso horário do Brasil (America/Sao_Paulo)
    const todayStr = getLocalDateString(referenceDate || new Date());

    if (promo.startDate && todayStr < promo.startDate) {
      return false; // Ainda não começou
    }

    if (todayStr > promo.endDate) {
      return false; // Já expirou
    }
  }

  return true;
}

/**
 * Retorna o preço efetivo a ser cobrado:
 * Se a promoção estiver ativa e for menor que o preço original, retorna o promocional.
 * Caso contrário, retorna o preço original.
 */
export function getEffectivePrice(
  originalPrice: number,
  promo?: ServicePromotion | null,
  referenceDate?: string | Date
): number {
  const base = Number(originalPrice || 0);
  if (isPromoActive(promo, referenceDate) && promo && promo.promoPrice < base) {
    return Number(promo.promoPrice);
  }
  return base;
}

/**
 * Retorna o texto formatado do gatilho de escassez ou validade.
 */
export function getPromoUrgencyText(promo?: ServicePromotion | null): string | null {
  if (!promo || !promo.active) return null;

  const limitType: PromoLimitType = promo.limitType || 'none';

  if (limitType === 'quantity' && promo.maxUsage) {
    const max = Number(promo.maxUsage || 0);
    const current = Number(promo.currentUsage || 0);
    const remaining = Math.max(0, max - current);

    if (remaining === 0) return 'Vagas promocionais esgotadas';
    if (remaining === 1) return '⚠️ Resta apenas 1 vaga!';
    if (remaining <= 5) return `🔥 Restam apenas ${remaining} vagas!`;
    return `⚡ Vagas limitadas (${remaining} restantes)`;
  }

  if (limitType === 'date' && promo.endDate) {
    try {
      const [year, month, day] = promo.endDate.split('-');
      const todayStr = getLocalDateString();
      if (todayStr === promo.endDate) {
        return '⏳ Último dia hoje!';
      }
      return `⏳ Válido até ${day}/${month}`;
    } catch {
      return `⏳ Válido até ${promo.endDate}`;
    }
  }

  if (promo.title && promo.title.trim()) {
    return `🔥 ${promo.title.trim()}`;
  }

  return '⚡ Oferta Especial';
}

/**
 * Verifica se um nicho é focado em agendamento / atendimento direto (Barbearia, Salão, Estética, Pet Shop, etc.)
 */
export function isSchedulingNiche(niche?: string, profession?: string): boolean {
  const combined = `${niche || ''} ${profession || ''}`.toLowerCase();

  return (
    combined.includes('barbe') ||
    combined.includes('salao') ||
    combined.includes('salão') ||
    combined.includes('cabel') ||
    combined.includes('estet') ||
    combined.includes('manicur') ||
    combined.includes('unha') ||
    combined.includes('pedicur') ||
    combined.includes('lash') ||
    combined.includes('cilio') ||
    combined.includes('sobrancelh') ||
    combined.includes('massag') ||
    combined.includes('massoterap') ||
    combined.includes('podolog') ||
    combined.includes('depila') ||
    combined.includes('maquiag') ||
    combined.includes('make') ||
    combined.includes('pet') ||
    combined.includes('tosa') ||
    combined.includes('banho') ||
    combined.includes('fisioterap') ||
    combined.includes('terapia') ||
    combined.includes('personal') ||
    combined.includes('tatuag') ||
    combined.includes('piercing')
  );
}

/**
 * Gera mensagem formatada pronta para divulgação rápida no WhatsApp.
 */
export function generatePromoWhatsAppText(params: {
  businessName?: string;
  serviceName: string;
  originalPrice: number;
  promoPrice: number;
  promoTitle?: string;
  bookingUrl?: string;
  urgencyNote?: string | null;
}): string {
  const discountPercent = calculateDiscountPercent(params.originalPrice, params.promoPrice);
  const discountAmount = calculateDiscountAmount(params.originalPrice, params.promoPrice);

  const greeting = params.businessName
    ? `Olá! Mensagem especial da *${params.businessName}*`
    : `Olá! Temos uma novidade especial para você`;

  const promoHeader = params.promoTitle
    ? `🔥 *${params.promoTitle.toUpperCase()}*`
    : `🔥 *CONDIÇÃO ESPECIAL DA SEMANA*`;

  const priceLine = `De ~${formatCurrencyBRL(params.originalPrice)}~ por apenas *${formatCurrencyBRL(params.promoPrice)}* (${discountPercent}% OFF • Economia de ${formatCurrencyBRL(discountAmount)})`;

  const urgencyLine = params.urgencyNote ? `\n\n${params.urgencyNote}` : '';

  const bookingLine = params.bookingUrl
    ? `\n\n👉 *Agende seu horário online:*\n${params.bookingUrl}`
    : `\n\n👉 Responda esta mensagem para garantir seu horário!`;

  return `${greeting}\n\n${promoHeader}\n\n✂️ *${params.serviceName}*\n💰 ${priceLine}${urgencyLine}${bookingLine}`;
}
